/**
 * Cohérence entre les fabrications FAB observées au journal et les liens
 * déclarés dans les tables de copacking (règle 15, actée le 02/09/2026).
 *
 * Les FAB font foi opérationnellement : ce sont elles qui décrivent ce qui a
 * réellement été consommé. La table reste le référentiel déclaré. La
 * comparaison se juge donc **au niveau famille** — consommer un jumeau du
 * composant déclaré, c'est consommer le même article physique, et cela n'a
 * rien d'une anomalie.
 *
 * Le contrôle est **informatif** : il ne bloque rien, il donne à lire.
 */
export const COHERENCE_STATUTS = [
    'CONFORME',
    'CONFORME_FAMILLE',
    'NON_DECLARE',
    'SANS_TABLE',
];
export const COHERENCE_STATUT_LABELS = {
    CONFORME: 'Conforme',
    CONFORME_FAMILLE: 'Conforme par famille',
    NON_DECLARE: 'Lien copacking non déclaré',
    SANS_TABLE: 'Composé absent des tables',
};
export function controlerCoherenceCopacking(params) {
    const triees = [...params.fabrications].sort((a, b) => (a.jour ?? '').localeCompare(b.jour ?? '') || a.reference.localeCompare(b.reference));
    const lignes = [];
    for (const fabrication of triees) {
        const declares = params.liensDeclares.get(fabrication.compose) ?? [];
        for (const composant of fabrication.composants) {
            const base = {
                reference: fabrication.reference,
                jour: fabrication.jour,
                compose: fabrication.compose,
                composant,
                composantsDeclares: [...declares],
            };
            if (declares.length === 0) {
                // La table est muette sur ce composé : elle n'est pas contredite pour
                // autant. Confondre les deux ferait passer un trou de référentiel pour
                // une divergence de fabrication.
                lignes.push({ ...base, statut: 'SANS_TABLE', declareEquivalent: null });
                continue;
            }
            if (declares.includes(composant)) {
                lignes.push({ ...base, statut: 'CONFORME', declareEquivalent: null });
                continue;
            }
            // Un jumeau vaut le code déclaré. Sans famille, deux codes orphelins
            // restent deux codes différents : l'absence de famille ne rapproche rien.
            const famille = params.familleParCodpro.get(composant);
            const equivalent = famille === undefined
                ? undefined
                : declares.find((declare) => params.familleParCodpro.get(declare) === famille);
            lignes.push(equivalent === undefined
                ? { ...base, statut: 'NON_DECLARE', declareEquivalent: null }
                : { ...base, statut: 'CONFORME_FAMILLE', declareEquivalent: equivalent });
        }
    }
    return lignes;
}
/**
 * Composés déclarés à tort en comptabilité matières (règle 15, modèle
 * consolidé le 04/09/2026).
 *
 * L'expédition d'un composé est déclarée **sous le code composant** : « toujours
 * rattaché à la référence père ». Le composé ne doit donc porter aucune ligne
 * GEJNM — vérifié sur 14 composés du fichier réel, un seul y figure (435954,
 * 216 lignes), et c'est une anomalie d'instruction historique.
 *
 * Le contrôle est **informatif** : il ne corrige rien, il nomme un cas à
 * instruire avec KN.
 */
export function anomaliesDeclarationCompose(params) {
    const anomalies = [];
    for (const compose of new Set(params.composes)) {
        const declaration = params.declarations.get(compose);
        if (declaration === undefined || declaration.lignes === 0)
            continue;
        anomalies.push({ compose, ...declaration });
    }
    // Le cas le plus bavard en premier : c'est celui qui pèse dans le dossier.
    return anomalies.sort((a, b) => b.lignes - a.lignes || a.compose.localeCompare(b.compose));
}
//# sourceMappingURL=copacking-coherence.js.map